import { useMemo, useState } from "react";
import {
  apiErrorMessage,
  assignTaskEmployee,
  createOtherTask,
  createOtherTaskType,
  createRecurringTemplate,
  updateRecurringTemplate,
} from "../../../api";
import { todayIso } from "../bookkeeping";

// One form for both kinds of bookkeeping work:
//   * Repeats monthly/quarterly/annually -> a recurring TEMPLATE; the server
//     makes its first period straight away and the rest on the calendar.
//   * Does not repeat -> a single one-off task.
// Passing `template` edits that template (its schedule is locked once it has
// periods, so only name, due rule and owners are editable then).

const field = { display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "#676879" };
const input = {
  border: "1px solid #d0d4e4", borderRadius: 6, padding: "7px 9px",
  fontSize: 13, color: "#323338", background: "#fff",
};

function monthOf(iso) {
  return (iso || "").slice(0, 7);
}

export default function WorkFormModal({
  clients = [], employees = [], taskTypes = [], setTaskTypes,
  template = null, onClose, onSaved,
}) {
  const editing = Boolean(template);
  const locked = editing && (template.open_period_count > 0 || template.latest_period_label);
  const bkTypes = useMemo(
    () => taskTypes.filter((t) => t.is_bookkeeping || /bookkeep|\bpst\b|\bgst\b|\bhst\b/i.test(t.name)),
    [taskTypes]);

  const [clientName, setClientName] = useState(template?.client_name || "");
  const [typeName, setTypeName] = useState(template?.task_type_name || bkTypes[0]?.name || "Bookkeeping");
  const [name, setName] = useState(template?.name || "");
  const [recurrence, setRecurrence] = useState(template?.recurrence || "monthly");
  const [firstMonth, setFirstMonth] = useState(monthOf(template?.first_period_start) || monthOf(todayIso()));
  const [dueDays, setDueDays] = useState(template ? String(template.due_days_after) : "30");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [ownerIds, setOwnerIds] = useState((template?.owner_ids || []).map(Number));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const people = employees.filter((e) => e.is_active !== false);

  const resolveClient = () => {
    const n = clientName.trim().toLowerCase();
    return clients.find((c) => (c.name || "").toLowerCase() === n) || null;
  };

  const resolveType = async () => {
    const n = typeName.trim();
    if (!n) return null;
    const hit = taskTypes.find((t) => t.name.toLowerCase() === n.toLowerCase());
    if (hit) return hit.id;
    const created = await createOtherTaskType({ name: n, is_bookkeeping: true });
    setTaskTypes?.((prev) => [...prev, created]);
    return created.id;
  };

  const save = async () => {
    setError("");
    const client = resolveClient();
    if (!client) { setError("Pick a client from the list."); return; }
    const workName = name.trim() || typeName.trim();
    if (!workName) { setError("Give the work a name."); return; }
    setSaving(true);
    try {
      const typeId = await resolveType();
      if (recurrence !== "none") {
        const payload = {
          client: client.id, task_type: typeId, name: workName,
          due_days_after: Math.max(0, parseInt(dueDays, 10) || 0),
          owner_ids: ownerIds,
        };
        payload.recurrence = recurrence;
        if (!locked) payload.first_period_start = `${firstMonth}-01`;
        if (editing) await updateRecurringTemplate(template.id, payload);
        else await createRecurringTemplate(payload);
      } else {
        const payload = {
          name: workName, client: client.id, task_type: typeId,
          recurrence: "none", due_date: dueDate || null,
        };
        if (periodFrom) payload.period_start = `${periodFrom}-01`;
        if (periodFrom && periodTo) {
          const [y, m] = periodTo.split("-").map(Number);
          const last = new Date(y, m, 0).getDate();
          payload.period_end = `${periodTo}-${String(last).padStart(2, "0")}`;
        }
        const created = await createOtherTask(payload);
        for (const id of ownerIds) await assignTaskEmployee(created.id, id);
      }
      await onSaved();
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        role="dialog" aria-modal="true"
        aria-label={editing ? "Edit recurring template" : "New bookkeeping task"}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, padding: "22px 26px", width: 480,
          maxHeight: "86vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          display: "flex", flexDirection: "column", gap: 14,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 17 }}>
          {editing ? "Edit recurring template" : "New bookkeeping task"}
        </h3>

        <label style={field}>
          Client
          <input
            list="bk-clients" value={clientName} disabled={editing}
            onChange={(e) => setClientName(e.target.value)} style={input}
            placeholder="Start typing a client…"
          />
          <datalist id="bk-clients">
            {clients.map((c) => <option key={c.id} value={c.name} />)}
          </datalist>
        </label>

        <div style={{ display: "flex", gap: 12 }}>
          <label style={{ ...field, flex: 1 }}>
            Type
            <input list="bk-types" value={typeName} onChange={(e) => setTypeName(e.target.value)} style={input} />
            <datalist id="bk-types">
              {bkTypes.map((t) => <option key={t.id} value={t.name} />)}
            </datalist>
          </label>
          <label style={{ ...field, flex: 1 }}>
            Work item
            <input
              value={name} onChange={(e) => setName(e.target.value)} style={input}
              placeholder={typeName || "e.g. PST return"}
            />
          </label>
        </div>

        <label style={field}>
          Repeats
          <select
            value={recurrence}
            onChange={(e) => {
              const next = e.target.value;
              setRecurrence(next);
              // Quarters and years default to the calendar ones.
              const [y, m] = firstMonth.split("-").map(Number);
              if (next === "quarterly") setFirstMonth(`${y}-${String(Math.floor((m - 1) / 3) * 3 + 1).padStart(2, "0")}`);
              if (next === "annual") setFirstMonth(`${y}-01`);
            }}
            style={input}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annually</option>
            {!editing && <option value="none">Does not repeat</option>}
          </select>
        </label>

        {recurrence !== "none" ? (
          <div style={{ display: "flex", gap: 12 }}>
            <label style={{ ...field, flex: 1 }}>
              First period starts
              <input
                type="month" value={firstMonth} disabled={locked}
                onChange={(e) => setFirstMonth(e.target.value)} style={input}
              />
            </label>
            <label style={{ ...field, flex: 1 }}>
              Due (days after period ends)
              <input
                type="number" min="0" value={dueDays}
                onChange={(e) => setDueDays(e.target.value)} style={input}
              />
            </label>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 12 }}>
            <label style={{ ...field, flex: 1 }}>
              Period from
              <input type="month" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} style={input} />
            </label>
            <label style={{ ...field, flex: 1 }}>
              to
              <input type="month" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} style={input} />
            </label>
            <label style={{ ...field, flex: 1 }}>
              Due
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={input} />
            </label>
          </div>
        )}
        {locked && (
          <div style={{ fontSize: 12, color: "#676879" }}>
            This template already has periods. Changing how often it repeats applies
            to future periods, starting after the latest one; existing periods keep their dates.
          </div>
        )}

        <div style={field}>
          Owner
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {people.map((e) => {
              const id = Number(e.id);
              const on = ownerIds.includes(id);
              const nm = e.name || e.full_name || e.user?.username || "";
              return (
                <button
                  key={id} type="button"
                  onClick={() => setOwnerIds((p) => (on ? p.filter((x) => x !== id) : [...p, id]))}
                  aria-pressed={on}
                  style={{
                    borderRadius: 999, padding: "4px 10px", fontSize: 12, cursor: "pointer",
                    border: on ? "1px solid #00854d" : "1px solid #d0d4e4",
                    background: on ? "#e5f6ed" : "#fff", color: "#323338",
                  }}
                >{nm}</button>
              );
            })}
          </div>
        </div>

        {error && <div role="alert" style={{ color: "#d83a52", fontSize: 13 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button" onClick={onClose}
            style={{ border: "1px solid #d0d4e4", background: "#fff", borderRadius: 6, padding: "7px 14px", cursor: "pointer" }}
          >Cancel</button>
          <button
            type="button" onClick={save} disabled={saving}
            style={{
              border: "none", background: "#00854d", color: "#fff", borderRadius: 6,
              padding: "7px 14px", cursor: saving ? "default" : "pointer", fontWeight: 500,
            }}
          >{saving ? "Saving…" : editing ? "Save" : recurrence === "none" ? "Create task" : "Create template"}</button>
        </div>
      </div>
    </div>
  );
}
